import React from 'react';
import useReveal from '../../AIEmployees/useReveal';
import { trackEvent } from '../../../utils/analytics';
import './A2AHowItWorks.css';

/**
 * A2AHowItWorks — "Two ways in" (Nitish, Oct 10 2026): the API key (scripts, SDK, OpenClaw, Hermes) and the
 * MCP connector (Claude, Cursor, Codex, any MCP client) side by side, three steps and a snippet each.
 * The page title/meta stay as they were: MCP search traffic comes through the blog (his Oct 6 ruling), and the
 * link text "CellCog MCP server" points at that post (Rhea's SEO ask). Styles: AgentToAgentPage.css (.a2a-how*).
 */
const PY_SNIPPET =
  '<span class="tok-com"># Your agent delegates to CellCog: any modality, one call</span>\n'
  + '<span class="tok-kw">from</span> cellcog <span class="tok-kw">import</span> CellCogClient\n\n'
  + '<span class="tok-com"># Reads CELLCOG_API_KEY from the environment</span>\n'
  + 'client = CellCogClient(agent_provider=<span class="tok-str">"openclaw"</span>)\n\n'
  + 'result = client.create_chat(\n'
  + '    prompt=<span class="tok-str">"Research the EV battery market and make a 10-page PDF"</span>,\n'
  + '    task_label=<span class="tok-str">"ev-battery-research"</span>,\n'
  + '    chat_mode=<span class="tok-str">"agent"</span>,\n'
  + ')\n'
  + '<span class="tok-kw">print</span>(result[<span class="tok-str">"chat_id"</span>])  <span class="tok-com"># files delivered on completion</span>';

const MCP_SNIPPET =
  '<span class="tok-com"># Claude Code, one line. Claude web, Desktop, Cursor, Codex:</span>\n'
  + '<span class="tok-com"># add a remote MCP server with this URL.</span>\n'
  + 'claude mcp add --transport http cellcog \\\n'
  + '  <span class="tok-str">https://cellcog.ai/mcp</span>\n\n'
  + '<span class="tok-com"># First use opens CellCog sign-in. Approve once.</span>';

const WAYS = [
  {
    key: 'api-key',
    label: 'API key',
    title: 'One key for your code',
    forLine: 'Scripts, the Python SDK, OpenClaw, Hermes, your own agents',
    steps: [
      { n: '01', title: 'Get one API key', body: 'A single key unlocks every capability: research, video, PDFs, apps, dashboards.' },
      { n: '02', title: 'Call from any agent', body: 'One unified interface, no per-capability plumbing.' },
      { n: '03', title: 'Get back finished work', body: 'Your agent receives a finished artifact, the same output a human would get.' },
    ],
    codeTitle: 'your_agent.py',
    code: PY_SNIPPET,
  },
  {
    key: 'mcp',
    label: 'MCP connector',
    title: 'No key. Sign in once.',
    forLine: 'Claude (web, Desktop, Cowork, Code), Cursor, Codex, any MCP client',
    steps: [
      { n: '01', title: 'Add the connector', body: <>Paste <code>https://cellcog.ai/mcp</code> into your client.</> },
      { n: '02', title: 'Sign in and approve', body: 'Once, on CellCog. Nothing to copy or store.' },
      { n: '03', title: 'Get back finished work', body: 'Your assistant briefs CellCog and gets the files back as links.' },
    ],
    codeTitle: 'terminal',
    code: MCP_SNIPPET,
  },
];

const A2AHowItWorks = () => {
  const [ref, visible] = useReveal();
  const seenRef = React.useRef(false);
  React.useEffect(() => {
    if (visible && !seenRef.current) {
      seenRef.current = true;
      trackEvent('section_view', { section: 'how-it-works', page: 'agent-to-agent' });
    }
  }, [visible]);
  const linkClick = (name) => () => trackEvent('cta_click', { cta_name: name, location: 'a2a_how' });
  return (
    <section className="a2a-how aie-section" id="a2a-how" ref={ref}>
      <div className="aie-section-head">
        <h2 className="aie-section-title aie-display">Two ways in. Any agent. Every capability.</h2>
        <p className="aie-section-sub">
          Use an API key from scripts and SDKs, or connect over MCP from the assistant you already use.
          Same platform, same finished work.
        </p>
      </div>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="a2a-how__ways">
          {WAYS.map((w) => (
            <div className="a2a-how__way" key={w.key}>
              <span className="a2a-how__label">{w.label}</span>
              <h3 className="a2a-how__way-title">{w.title}</h3>
              <p className="a2a-how__for">{w.forLine}</p>
              <ol className="a2a-how__steps">
                {w.steps.map((s) => (
                  <li className="a2a-how__step" key={s.n}>
                    <span className="a2a-how__num">{s.n}</span>
                    <div>
                      <h4 className="a2a-how__step-title">{s.title}</h4>
                      <p className="a2a-how__step-body">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="a2a-how__code">
                <div className="a2a-how__code-head">
                  <span className="a2a-how__code-dot" />
                  <span className="a2a-how__code-dot" />
                  <span className="a2a-how__code-dot" />
                  <span className="a2a-how__code-title">{w.codeTitle}</span>
                </div>
                <pre dangerouslySetInnerHTML={{ __html: w.code }} />
              </div>
            </div>
          ))}
        </div>
        <p className="a2a-how__more">
          Setup for every client:{' '}
          <a href="/support/mcp-connector-guide" onClick={linkClick('mcp_guide')}>MCP connector guide</a>
          {' · '}What shipped:{' '}
          <a href="/blog/cellcog-mcp-server/" onClick={linkClick('mcp_blog')}>CellCog MCP server</a>
        </p>
      </div>
    </section>
  );
};

export default A2AHowItWorks;
